import React from "react";
import Footer from "@insightfulscience/atomic-react/Footer";
import FooterContainer from "@insightfulscience/atomic-react/Footer/Container";
import FooterMainContainer from "@insightfulscience/atomic-react/Footer/Main";
import FooterNav, {
	FooterNavCol,
	FooterNavItemLink,
	FooterNavList,
	FooterNavListItem,
	FooterNavListTitle,
	FooterNavRow,
} from "@insightfulscience/atomic-react/Footer/Nav";
import FooterSideBarContainer from "@insightfulscience/atomic-react/Footer/Sidebar";
import FooterCopyright, {
	FooterCopyrightLink,
	FooterCopyrightText,
} from "@insightfulscience/atomic-react/Footer/Copyright";
import FooterLogo from "@insightfulscience/atomic-react/Footer/Logo";
import FooterSocial, {
	FooterSocialItemLink,
} from "@insightfulscience/atomic-react/Footer/Social";
import NextLink from "@insightfulscience/smart-react/NextLink";
import { useLocale } from "@insightfulscience/i18";
import Html from "@insightfulscience/smart-react/Html";
import OneTrustCookieSettingsButton from "@src/components/OneTrustCookieSettingsButton";
import routes from "../../routes";
import { isRouteEnabled } from "../../utils/routing";

function SiteFooter() {
	const [t] = useLocale("common");

	return (
		<Footer>
			<FooterContainer>
				<FooterMainContainer>
					<FooterNav>
						<FooterNavRow data-qa-selector="footerNavList" lgCols={5}>
							<FooterNavCol md={3}>
								<FooterNavListTitle data-qa-selector="productTitleFt">
									{t("MENU.PRODUCT")}
								</FooterNavListTitle>
								<FooterNavList>
									<FooterNavListItem enable={isRouteEnabled(routes.features)}>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.product.subRoutes.overview.url}
										>
											{t("MENU.dropdown.product.OVERVIEW")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem
										enable={
											isRouteEnabled(routes.features) ||
											isRouteEnabled(routes.product.subRoutes.classicAnalysis)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.product.subRoutes.classicAnalysis.url}
										>
											{t("MENU.dropdown.product.CLASSICAL_ANALYSIS")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem
										enable={
											isRouteEnabled(routes.features) ||
											isRouteEnabled(
												routes.product.subRoutes.highDimensionalAnalysis,
											)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={
												routes.product.subRoutes.highDimensionalAnalysis.url
											}
										>
											{t("MENU.ADVANCED_ANALYSIS")}
										</NextLink>
									</FooterNavListItem>

									<FooterNavListItem
										enable={
											isRouteEnabled(routes.features) ||
											isRouteEnabled(routes.product.subRoutes.dataManagement)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.product.subRoutes.dataManagement.url}
										>
											{t("MENU.dropdown.product.DATA_MANAGEMENT")}
										</NextLink>
									</FooterNavListItem>

									<FooterNavListItem
										enable={
											isRouteEnabled(routes.features) ||
											isRouteEnabled(routes.product.subRoutes.prismIntegration)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.product.subRoutes.prismIntegration.url}
										>
											{t("MENU.PRISM_INTEGRATION")}
										</NextLink>
									</FooterNavListItem>

									<FooterNavListItem
										enable={
											isRouteEnabled(routes.features) ||
											isRouteEnabled(
												routes.product.subRoutes.easyPanelIntegration,
											)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.product.subRoutes.easyPanelIntegration.url}
										>
											{t("MENU.EASY_PANEL_INTEGRATION")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem
										enable={
											isRouteEnabled(routes.features) ||
											isRouteEnabled(routes.updates)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.updates.url}
										>
											{t("MENU.dropdown.product.UPDATES")}
										</NextLink>
									</FooterNavListItem>
								</FooterNavList>
							</FooterNavCol>
							<FooterNavCol md={3}>
								<FooterNavListTitle data-qa-selector="solutionsTitleFt">
									{t("MENU.SOLUTIONS")}
								</FooterNavListTitle>
								<FooterNavList>
									<FooterNavListItem>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.solutions.subRoutes.enterprise.url}
										>
											{t("MENU.dropdown.solutions.ENTERPRISE")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.solutions.subRoutes.customAutogating.url}
										>
											{t("MENU.dropdown.solutions.CUSTOM_AUTOGATING")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.solutions.subRoutes.omiqLuma.url}
										>
											{t("MENU.dropdown.solutions.OMIQ_LUMA")}
										</NextLink>
									</FooterNavListItem>
								</FooterNavList>
							</FooterNavCol>
							<FooterNavCol md={3}>
								<FooterNavListTitle data-qa-selector="getOMIQTitleFt">
									{t("MENU.GET")}
								</FooterNavListTitle>
								<FooterNavList>
									<FooterNavListItem enable={isRouteEnabled(routes.freeTrial)}>
										<FooterNavItemLink href={routes.freeTrial.url}>
											{t("MENU.FREE_TRIAL")}
										</FooterNavItemLink>
									</FooterNavListItem>
									<FooterNavListItem enable={isRouteEnabled(routes.pricing)}>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.pricing.url}
										>
											{t("MENU.PRICING")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem
										enable={isRouteEnabled(routes.quoteRequest)}
									>
										<FooterNavItemLink href={routes.quoteRequest.url}>
											{t("MENU.QUOTE_REQUEST")}
										</FooterNavItemLink>
									</FooterNavListItem>
								</FooterNavList>
							</FooterNavCol>
							<FooterNavCol md={3}>
								<FooterNavListTitle data-qa-selector="supportTitleFt">
									{t("MENU.SUPPORT")}
								</FooterNavListTitle>
								<FooterNavList>
									<FooterNavListItem
										enable={
											isRouteEnabled(routes.guides) ||
											isRouteEnabled(routes.guides.subRoutes.tutorial)
										}
									>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.guides.subRoutes.tutorial.url}
										>
											{t("MENU.TUTORIAL")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem enable={isRouteEnabled(routes.resources)}>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.resources.url}
										>
											{t("MENU.RESOURCE_CENTER")}
										</NextLink>
									</FooterNavListItem>
									<FooterNavListItem enable={isRouteEnabled(routes.helpCenter)}>
										<NextLink
											Component={FooterNavItemLink}
											href={routes.resources.subRoutes.userGuides.url}
										>
											{t("MENU.dropdown.resources.USER_GUIDES")}
										</NextLink>
									</FooterNavListItem>

									<FooterNavListItem
										enable={isRouteEnabled(routes.contactSupport)}
									>
										<FooterNavItemLink href={routes.contactSupport.url}>
											{t("MENU.CONTACT_SUPPORT")}
										</FooterNavItemLink>
									</FooterNavListItem>
								</FooterNavList>
							</FooterNavCol>
							<FooterNavCol md={3}>
								<FooterNavListTitle data-qa-selector="companyTitleFt">
									{t("MENU.COMPANY")}
								</FooterNavListTitle>
								<FooterNavList>
									<FooterNavList>
										<FooterNavListItem enable={isRouteEnabled(routes.about)}>
											<NextLink
												Component={FooterNavItemLink}
												href={routes.about.url}
											>
												{t("MENU.ABOUT")}
											</NextLink>
										</FooterNavListItem>
										<FooterNavListItem
											enable={isRouteEnabled(routes.dotmatics)}
										>
											<FooterNavItemLink href={routes.dotmatics.url}>
												{t("MENU.DOTMATICS")}
											</FooterNavItemLink>
										</FooterNavListItem>
										<FooterNavListItem
											enable={isRouteEnabled(routes.portfolio)}
										>
											<FooterNavItemLink href={routes.portfolio.url}>
												{t("MENU.PORTFOLIO")}
											</FooterNavItemLink>
										</FooterNavListItem>
										<FooterNavListItem enable={isRouteEnabled(routes.careers)}>
											<FooterNavItemLink href={routes.careers.url}>
												{t("MENU.CAREERS")}
											</FooterNavItemLink>
										</FooterNavListItem>
										<FooterNavListItem enable={isRouteEnabled(routes.about)}>
											<NextLink
												Component={FooterNavItemLink}
												href={routes.about.subRoutes.contact.url}
											>
												{t("MENU.CONTACT")}
											</NextLink>
										</FooterNavListItem>
									</FooterNavList>
								</FooterNavList>
							</FooterNavCol>
						</FooterNavRow>
					</FooterNav>
				</FooterMainContainer>
				<FooterSideBarContainer>
					<FooterCopyright>
						<FooterLogo
							logoPath="/images/dotmatics/omiq/logo/logo-omiq.svg"
							logoAlt=""
							data-qa-selector="logoFt"
						/>
						<FooterCopyrightText>
							<FooterCopyrightText.CompanyName>
								&copy; {new Date().getFullYear()} Dotmatics
							</FooterCopyrightText.CompanyName>
							<FooterCopyrightLink
								href={routes.external.privacyPolicy}
								spacing={{ mb: 2 }}
								target="_blank"
								data-qa-selector="privacyPolicyFt"
							>
								{t("MENU.PRIVACY_POLICY")}
							</FooterCopyrightLink>
							<br />
							<NextLink
								Component={FooterCopyrightLink}
								href={routes.termsOfService.url}
								spacing={{ mb: 2 }}
								data-qa-selector="termsOfServiceFt"
							>
								{t("MENU.TERMS_OF_SERVICE")}
							</NextLink>
							<br />
							<FooterCopyrightLink
								target="_blank"
								href={routes.external.doNotSell}
								spacing={{ my: 2 }}
								data-qa-selector="dontSellFt"
							>
								<Html Tag="span">{t("MENU.DO_NOT_SELL")}</Html>
							</FooterCopyrightLink>
							<br />
							<OneTrustCookieSettingsButton>
								Cookies Settings
							</OneTrustCookieSettingsButton>
							<br />
							<FooterCopyrightLink
								target="_blank"
								href={routes.external.trademarks}
								spacing={{ mt: 2 }}
							>
								{t("MENU.TRADEMARKS")}
							</FooterCopyrightLink>
							<br />
							<FooterCopyrightLink
								target="_blank"
								href={routes.external.whistleblowing}
								spacing={{ my: 2 }}
							>
								{t("MENU.WHISTLEBLOWING")}
							</FooterCopyrightLink>
						</FooterCopyrightText>
						<FooterSocial>
							<FooterSocialItemLink
								target="_blank"
								href={routes.twitter}
								name="twitter-square"
								data-qa-selector="twitterFt"
							/>
							<FooterSocialItemLink
								target="_blank"
								href={routes.linkedIn}
								name="linkedin-square"
								data-qa-selector="linkedInFt"
							/>
						</FooterSocial>
					</FooterCopyright>
				</FooterSideBarContainer>
			</FooterContainer>
		</Footer>
	);
}

export default SiteFooter;
